import { useState, useEffect } from "react";
import { getDoc, setDoc, deleteDoc, doc, serverTimestamp, collection, query, where, onSnapshot } from "firebase/firestore";
import { useApp } from "../../../context/AppContext";
import { useAuth } from "../../../context/AuthContext";
import { db } from "../../../firebase";
import { useNavigate } from "react-router-dom";
import { AGENCIAS, METODOS } from "../services/parseDescripcionPedido";
import PagoModal from "../components/PagoModal";

const METODO_ICON = { AGENCIA: "🏢", RETIRA: "🤝", CAMION: "🚛", GIRA: "🚚" };

// TODO: RUT del cliente — en standby hasta integración con Finnegans
// const [clienteRUT, setClienteRUT] = useState("");

export default function ConfirmarDespacho({ pedidoId, onDone, compact = false }) {
  const { toast, haptics } = useApp();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [pedido, setPedido]             = useState(null);
  const [agencia, setAgencia]           = useState(null);
  const [showSelector, setShowSelector] = useState(false);
  const [showMetodo, setShowMetodo]     = useState(false);
  const [loading, setLoading]           = useState(false);
  const [loadingData, setLoadingData]   = useState(true);
  const [despachado, setDespachado]     = useState(false);

  // Viaje (GIRA/CAMIÓN): ventas debe elegir a qué viaje va el pedido al despachar.
  const [viajes, setViajes]     = useState([]);   // camiones/giras activos del depósito
  const [viajeSel, setViajeSel] = useState(null);  // id del viaje elegido

  // Ventas puede cambiar el método de entrega antes de despachar (ej: una gira
  // pasa a "Retira" porque un vendedor se lo lleva al cliente). El cambio se
  // guarda en el doc despachado al confirmar.
  function cambiarMetodo(m) {
    setPedido(prev => ({ ...prev, metodoEntrega: m }));
    if (m !== "AGENCIA") setAgencia(null); // limpiamos agencia si ya no es por agencia
    if (m !== "GIRA" && m !== "CAMION") setViajeSel(null); // limpiamos viaje si ya no aplica
    setShowMetodo(false);
  }

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const snap = await getDoc(doc(db, "pedidos", String(pedidoId)));
        if (!cancelled && snap.exists()) {
          const data = snap.data();
          setPedido(data);
          setAgencia(data.agencia || null);
          // RUT standby: setClienteRUT(data.clienteRUT || "");
        }
      } finally {
        if (!cancelled) setLoadingData(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [pedidoId]);

  // Cargar viajes (GIRA/CAMIÓN) activos del depósito cuando el método lo pide.
  useEffect(() => {
    const metodo = pedido?.metodoEntrega;
    const dep = pedido?.deposito;
    if ((metodo !== "GIRA" && metodo !== "CAMION") || !dep) { setViajes([]); return; }
    const qy = query(collection(db, "camiones"), where("deposito", "==", dep));
    const unsub = onSnapshot(qy, (snap) => {
      const ayer = Date.now() - 24 * 60 * 60 * 1000;
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }))
        .filter(c => (c.tipo || "CAMION") === metodo && c.estado !== "CERRADO"
          && (c.fechaSalida?.seconds ? c.fechaSalida.seconds * 1000 : 0) >= ayer)
        .sort((a, b) => (a.fechaSalida?.seconds || 0) - (b.fechaSalida?.seconds || 0));
      setViajes(list);
    }, () => setViajes([]));
    return () => unsub();
  }, [pedido?.metodoEntrega, pedido?.deposito]);

  // Pre-seleccionar el viaje que el sync ya haya asignado (auto-match por código).
  useEffect(() => {
    if (pedido?.camionId && viajes.some(v => v.id === pedido.camionId)) {
      setViajeSel(pedido.camionId);
    }
  }, [pedido?.camionId, viajes]);

  async function onDespachar() {
    try {
      setLoading(true);
      const ref = doc(db, "pedidos", String(pedidoId));
      const snap = await getDoc(ref);
      if (!snap.exists()) { toast.error("El pedido no existe"); return; }
      const data = snap.data();

      // 🔒 SEGURIDAD: un pedido NO puede saltear el CONTROL. En R8 solo se despacha
      // cuando el encargado ya lo controló (estado CONTROLADO). ISABELA no tiene un
      // paso de control aparte (el operario finaliza en CONTROLADO), así que también
      // se exige CONTROLADO — pero por compat se acepta PREPARADO en ISABELA.
      const _esIsabelaGuard = String(data.deposito || "").toUpperCase() === "ISABELA";
      const _estadosOk = _esIsabelaGuard ? ["PREPARADO", "CONTROLADO"] : ["CONTROLADO"];
      if (!_estadosOk.includes(data.estado)) {
        toast.error("Este pedido todavía no pasó por el control del encargado. No se puede despachar hasta que sea controlado.");
        return;
      }

      // NOTA: las etiquetas (bultos/paquetes) se generan UNA sola vez, en el
      // CONTROL del encargado (encolarEtiquetas). Acá, en el despacho, ya NO se
      // vuelven a generar para no duplicarlas.

      // Si ya existe en pedidos_despachados (ej: sync re-creó el pedido en 'pedidos'
      // pero el despacho original sigue en pedidos_despachados), saltear el setDoc
      // para no pisar datos de control ya registrados y evitar el error de permisos.
      // Método de entrega FINAL: el que ventas dejó en pantalla (puede haberlo
      // cambiado, ej: gira → retira). Ajustamos agencia/camionId en consecuencia.
      const metodoFinal = pedido?.metodoEntrega || data.metodoEntrega || null;
      const agenciaFinal = metodoFinal === "AGENCIA" ? (agencia || data.agencia || null) : null;
      const camionIdFinal = (metodoFinal === "GIRA" || metodoFinal === "CAMION") ? (viajeSel || data.camionId || null) : null;

      const despRef = doc(db, "pedidos_despachados", String(pedidoId));
      const despSnap = await getDoc(despRef);
      if (!despSnap.exists()) {
        await setDoc(despRef, {
          ...data,
          estado: "DESPACHADO",
          // clienteRUT standby — se agrega cuando se integre Finnegans
          metodoEntrega: metodoFinal,
          agencia: agenciaFinal,
          camionId: camionIdFinal,
          necesitaControl: true, // ambos depósitos (ISABELA y R8) requieren control de entrega
          despachadoAt: serverTimestamp(),
          despachadoPor: user?.uid || null,
          movedAt: serverTimestamp(),
          source: "app-despacho",
        });
      } else if (metodoFinal !== data.metodoEntrega) {
        // El despacho ya existía pero ventas cambió el método → lo actualizamos.
        await setDoc(despRef, { metodoEntrega: metodoFinal, agencia: agenciaFinal, camionId: camionIdFinal }, { merge: true });
      }

      await deleteDoc(ref);
      haptics?.success?.();
      toast.success("Pedido marcado como DESPACHADO");
      // El registro de pago (cobranza) es SOLO de ISABELA. En R8, ventas no carga
      // pago: se despacha y se vuelve directamente a la lista.
      const esIsabelaPed = String(data.deposito || "").toUpperCase() === "ISABELA";
      if (esIsabelaPed) {
        setDespachado(true); // abre el PagoModal obligatorio (ISABELA)
      } else {
        if (onDone) onDone(); else navigate("/pedidos");
      }
    } catch (e) {
      console.error(e);
      haptics?.error?.();
      toast.error(e?.message || "No se pudo despachar");
    } finally {
      setLoading(false);
    }
  }

  if (loadingData) {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: "10px", padding: "16px 0" }}>
        <div style={{ width: "18px", height: "18px", borderRadius: "50%", border: "2.5px solid #e2e8f0", borderTopColor: "#64748b", animation: "spin 0.7s linear infinite" }} />
        <span style={{ color: "#94a3b8", fontSize: "0.9rem" }}>Cargando pedido…</span>
      </div>
    );
  }

  const esAgencia = pedido?.metodoEntrega === "AGENCIA";
  const esViaje = pedido?.metodoEntrega === "GIRA" || pedido?.metodoEntrega === "CAMION";
  const tipoViaje = pedido?.metodoEntrega === "GIRA" ? "gira" : "camión";
  // Para despachar: si es AGENCIA hace falta la agencia; si es GIRA/CAMIÓN, el viaje.
  const puedeDespachar = (!esAgencia || agencia) && (!esViaje || viajeSel);

  const fmtFechaViaje = (ts) => {
    if (!ts?.seconds) return "";
    return new Date(ts.seconds * 1000).toLocaleDateString("es-UY", { day: "2-digit", month: "short" });
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>

      {/* ── Resumen del pedido (oculto en modo compact) ── */}
      {!compact && <div style={{
        background: "#f8fafc", border: "1.5px solid #e2e8f0",
        borderRadius: "12px", padding: "16px 18px",
      }}>
        <p style={{ margin: "0 0 2px", fontSize: "0.72rem", fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.06em" }}>
          Pedido listo para despachar
        </p>
        <p style={{ margin: 0, fontSize: "1.1rem", fontWeight: 800, color: "#0f172a" }}>
          #{pedido?.numero || pedidoId}
        </p>
        {pedido?.cliente && (
          <p style={{ margin: "4px 0 0", fontSize: "0.9rem", color: "#475569" }}>{pedido.cliente}</p>
        )}
        <div style={{ marginTop: "10px", display: "flex", flexWrap: "wrap", gap: "6px" }}>
          {pedido?.deposito && (
            <span style={{
              fontSize: "0.75rem", fontWeight: 600, padding: "3px 10px",
              borderRadius: "999px", background: "#f0fdf4", color: "#15803d",
              border: "1px solid #bbf7d0",
            }}>
              🏭 {pedido.deposito}
            </span>
          )}
        </div>
      </div>}

      {/* ── Método de entrega (editable por Ventas antes de despachar) ── */}
      <div style={{ background: "#fff", border: "1.5px solid #e2e8f0", borderRadius: "12px", padding: "14px 18px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px" }}>
          <div style={{ minWidth: 0 }}>
            <p style={{ margin: "0 0 4px", fontSize: "0.72rem", fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.06em" }}>
              Método de entrega
            </p>
            <span style={{ fontSize: "1rem", fontWeight: 800, color: "#0f172a" }}>
              {METODO_ICON[pedido?.metodoEntrega] || "📦"} {pedido?.metodoEntrega || "Sin definir"}
            </span>
          </div>
          <button
            onClick={() => setShowMetodo(s => !s)}
            style={{ flexShrink: 0, fontSize: "0.8rem", fontWeight: 600, color: "#1d4ed8", background: "none", border: "1px solid #bfdbfe", borderRadius: "8px", cursor: "pointer", padding: "6px 12px" }}
          >
            ✏️ Editar
          </button>
        </div>
        {showMetodo && (
          <div style={{ marginTop: "12px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
            {METODOS.map(m => {
              const sel = pedido?.metodoEntrega === m;
              return (
                <button
                  key={m}
                  onClick={() => cambiarMetodo(m)}
                  style={{
                    padding: "12px 10px", fontSize: "0.9rem", fontWeight: sel ? 800 : 600,
                    border: `1.5px solid ${sel ? "#2563eb" : "#e2e8f0"}`, borderRadius: "10px",
                    background: sel ? "#eff6ff" : "#fff", color: sel ? "#1d4ed8" : "#334155",
                    cursor: "pointer", display: "flex", alignItems: "center", gap: "8px",
                  }}
                >
                  <span>{METODO_ICON[m]}</span> {m}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Confirmación de agencia ── */}
      {esAgencia && (
        <div style={{
          background: agencia ? "#eff6ff" : "#fffbeb",
          border: `1.5px solid ${agencia ? "#bfdbfe" : "#fde68a"}`,
          borderRadius: "12px", padding: "14px 18px",
        }}>
          <p style={{ margin: "0 0 8px", fontSize: "0.72rem", fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.06em" }}>
            🚌 Agencia de envío
          </p>
          {agencia ? (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px" }}>
              <span style={{ fontSize: "1rem", fontWeight: 700, color: "#1d4ed8" }}>{agencia}</span>
              <button
                onClick={() => setShowSelector(true)}
                style={{ fontSize: "0.8rem", color: "#64748b", background: "none", border: "1px solid #e2e8f0", borderRadius: "6px", cursor: "pointer", padding: "4px 10px" }}
              >
                Cambiar
              </button>
            </div>
          ) : (
            <div>
              <p style={{ margin: "0 0 10px", fontSize: "0.85rem", color: "#b45309" }}>
                ⚠️ No se detectó la agencia. Seleccioná una para continuar.
              </p>
              <button
                onClick={() => setShowSelector(true)}
                style={{
                  width: "100%", padding: "10px 16px", fontSize: "0.88rem", fontWeight: 600,
                  background: "#fff", border: "1.5px solid #fbbf24", borderRadius: "8px",
                  color: "#b45309", cursor: "pointer",
                }}
              >
                Seleccionar agencia →
              </button>
            </div>
          )}
        </div>
      )}

      {/* ── Confirmación de viaje (GIRA / CAMIÓN) ── */}
      {esViaje && (
        <div style={{
          background: viajeSel ? "#eff6ff" : "#fffbeb",
          border: `1.5px solid ${viajeSel ? "#bfdbfe" : "#fde68a"}`,
          borderRadius: "12px", padding: "14px 18px",
        }}>
          <p style={{ margin: "0 0 8px", fontSize: "0.72rem", fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.06em" }}>
            {pedido?.metodoEntrega === "GIRA" ? "🚚 Gira" : "🚛 Camión"} — elegí el viaje
          </p>
          {viajes.length === 0 ? (
            <p style={{ margin: 0, fontSize: "0.85rem", color: "#b45309" }}>
              ⚠️ No hay {tipoViaje}s programados. Creá uno en “Giras y Camiones” antes de despachar este pedido.
            </p>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              {viajes.map(v => {
                const sel = viajeSel === v.id;
                return (
                  <button
                    key={v.id}
                    onClick={() => setViajeSel(v.id)}
                    style={{
                      display: "flex", alignItems: "center", justifyContent: "space-between", gap: "10px",
                      padding: "11px 13px", borderRadius: "10px",
                      border: `1.5px solid ${sel ? "#2563eb" : "#e2e8f0"}`,
                      background: sel ? "#eff6ff" : "#fff", cursor: "pointer", textAlign: "left",
                    }}
                  >
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: "0.9rem", color: sel ? "#1d4ed8" : "#0f172a" }}>
                        {sel ? "✓ " : ""}{v.descripcion}{v.codigo ? ` (${v.codigo})` : ""}
                      </div>
                      <div style={{ fontSize: "0.75rem", color: "#64748b" }}>Salida: {fmtFechaViaje(v.fechaSalida)}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── Botón despachar ── */}
      <button
        onClick={onDespachar}
        disabled={loading || !puedeDespachar}
        style={{
          padding: "15px 32px",
          fontSize: "0.95rem", fontWeight: 700,
          borderRadius: "10px", border: "none",
          background: (loading || !puedeDespachar) ? "#e2e8f0" : "#0f172a",
          color: (loading || !puedeDespachar) ? "#94a3b8" : "#fff",
          cursor: (loading || !puedeDespachar) ? "not-allowed" : "pointer",
          display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
          alignSelf: compact ? "stretch" : "flex-end",
          width: compact ? "100%" : undefined,
          minWidth: compact ? undefined : "180px",
          boxShadow: (loading || !puedeDespachar) ? "none" : "0 2px 8px rgba(15,23,42,0.18)",
          transition: "all 0.15s ease",
        }}
      >
        {loading ? (
          <>
            <span style={{ width: "14px", height: "14px", borderRadius: "50%", border: "2px solid #94a3b8", borderTopColor: "#fff", display: "inline-block" }} />
            Despachando…
          </>
        ) : (
          <>
            🚚 {esAgencia && !agencia
              ? "Elegí la agencia"
              : esViaje && !viajeSel
                ? `Elegí ${pedido?.metodoEntrega === "GIRA" ? "la gira" : "el camión"}`
                : esAgencia && agencia
                  ? `Despachar vía ${agencia}`
                  : esViaje && viajeSel
                    ? `Despachar en ${viajes.find(v => v.id === viajeSel)?.descripcion || tipoViaje}`
                    : "Confirmar despacho"}
          </>
        )}
      </button>

      {/* ── PagoModal obligatorio post-despacho (SOLO ISABELA) ── */}
      {despachado && String(pedido?.deposito || "").toUpperCase() === "ISABELA" && (
        <PagoModal
          pedidoId={String(pedidoId)}
          pedidoInfo={pedido}
          pagosActuales={[]}
          historial={[]}
          mandatory={true}
          onClose={() => {}}
          onSaved={() => {
            if (onDone) onDone(); else navigate("/pedidos");
          }}
        />
      )}

      {/* ── Modal selector de agencia ── */}
      {showSelector && (
        <div
          onClick={() => setShowSelector(false)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: "24px" }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: "#fff", borderRadius: "16px",
              padding: "24px", width: "100%", maxWidth: "480px",
              maxHeight: "70vh", overflowY: "auto",
              boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "18px" }}>
              <h3 style={{ margin: 0, fontSize: "1rem", fontWeight: 700, color: "#0f172a" }}>Seleccionar agencia</h3>
              <button onClick={() => setShowSelector(false)} style={{ background: "none", border: "none", fontSize: "22px", cursor: "pointer", color: "#94a3b8", lineHeight: 1 }}>×</button>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
              {AGENCIAS.map(ag => (
                <button
                  key={ag.nombre}
                  onClick={() => { setAgencia(ag.nombre); setShowSelector(false); }}
                  style={{
                    padding: "12px 10px", fontSize: "0.85rem",
                    fontWeight: agencia === ag.nombre ? 700 : 500,
                    border: "1.5px solid",
                    borderColor: agencia === ag.nombre ? "#2563eb" : "#e2e8f0",
                    borderRadius: "8px",
                    background: agencia === ag.nombre ? "#eff6ff" : "#fff",
                    color: agencia === ag.nombre ? "#1d4ed8" : "#334155",
                    cursor: "pointer", textAlign: "left",
                  }}
                >
                  <span style={{ fontSize: "0.68rem", color: "#94a3b8", display: "block", marginBottom: "2px" }}>{ag.orden}.</span>
                  {ag.nombre}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
